/*
 * The Private License
 *
 * Copyright (c) 2023 Mahon Media LLC.
 */

import React, {useEffect, useState} from "react";
import {
  Button,
  CircularProgress,
  OutlinedInput,
  Typography,
} from "@mui/material";
import {useHistory} from "react-router-dom";
import Cookies from "js-cookie";
import {useAuth} from "../contexts/AuthContext";
import {useNotification} from "../contexts/NotificationContext";
import {LOGIN_ERROR_MESSAGES} from "../constants/errorMessages";
import { getBackendBaseUrl, getOperaPopulateApiUrl } from "../utils/envUtils";

function Login() {
  const [loginData, setLoginData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const {login} = useAuth();
  const {setIsOpen, setIsFailed, setMessage} = useNotification();
  const token = Cookies.get("jwtToken");
  const host = getBackendBaseUrl();
  const loginEndpoint = `${host}/api/v1/auth/login`;
  useEffect(() => {
    if (token) history.push("/reservation");
  }, []);
  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setLoginData({...loginData, [name]: value});
  };

  async function handleLogin() {
    setIsLoading(true);
    try {
      if (loginData.username.length < 3) {
        throw new Error("Username length");
      }
      if (loginData.password.length < 8) {
        throw new Error("Pass length");
      }
      const response = await fetch(loginEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginData),
      });
      if (response.status === 401) {
        throw new Error(response.status);
      }
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const {access} = await response.json();
      const expirationDate = access.expires.replace(/\[[^\]]*\]/g, "");
      Cookies.set("jwtToken", access.token, {
        expires: new Date(expirationDate),
      });
      // See https://bitbucket.org/mahonmediallc/opera-populate-api/src/main/src/main/java/com/mahonmedia/opera/populate/web/restful/controller/AuthenticationPoint.java
      const operaHost = getOperaPopulateApiUrl();
      const operaResponse = await fetch(
        `${operaHost}/auth`,
        {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginData),
      });
      if (operaResponse.status === 401) {
        throw new Error(operaResponse.status);
      }
      if (!operaResponse.ok) {
        throw new Error("Network response was not ok");
      }
      const crew = await operaResponse.json();
      const crewExpires = crew.expires.replace(/\[[^\]]*\]/g, "");
      Cookies.set("crew_token", crew.access, {
        expires: new Date(crewExpires),
      });
      // See https://bitbucket.org/mahonmediallc/email-conversation/src/master/src/main/java/com/mahonmedia/beacon/email/api/AuthController.java
      const conversations = await fetch(
        `${operaHost}/codes/code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Auth-Token": crew.access
          }
      });
      if (conversations.status === 401) {
        throw new Error(conversations.status);
      }
      if (!conversations.ok) {
        throw new Error("Network response was not ok");
      }
      const code = await conversations.json();
      Cookies.set(
        "admin-dashboard", code.code,
        {
          expires: new Date(crewExpires)
        }
      );
      login();
      setIsOpen(true);
      setIsFailed(false);
      setMessage("You've successfully logged in");
      history.push("/app");
    } catch (error) {
      console.log(error.message);
      const errorMessage =
        LOGIN_ERROR_MESSAGES[error.message] || LOGIN_ERROR_MESSAGES.default;
      setIsOpen(true);
      setIsFailed(true);
      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: "flex",
        flexDirection: "column",
        width: "43%",
        margin: "0 auto",
        textAlign: "center",
        padding: "96px 0",
      }}
    >
      <Typography
        variant="h4"
        style={{
          fontWeight: "300",
          fontSize: "40px",
        }}
        gutterBottom
      >
        Login
      </Typography>

      <OutlinedInput
        sx={{mt: 2, "& input": {padding: "0.375rem 0.75rem"}}}
        type="text"
        placeholder="Username"
        name="username"
        required
        value={loginData.username}
        onChange={handleInputChange}
      />
      <OutlinedInput
        sx={{mt: 2, "& input": {padding: "0.375rem 0.75rem"}}}
        type="password"
        name="password"
        placeholder="Password"
        required
        value={loginData.password}
        onChange={handleInputChange}
      />

      {isLoading ? (
        <CircularProgress sx={{display: "block", m: "0 auto", mt: 5}}/>
      ) : (
        <Button
          variant="contained"
          sx={{
            mt: 3,
            alignSelf: "center",
            backgroundColor: "#6c757d",
            fontSize: "16px",
            "&:hover": {
              bgcolor: "#5c636a",
            },
            textTransform: "none",
          }}
          type="submit"
        >
          Submit
        </Button>
      )}
    </form>
  );
}

export default Login;
